import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { fmtWIB, fmtDuration } from "@/lib/dateFmt";
import { responseLanguageLabel, responseLanguageName } from "@/lib/responseLanguage";

export default function ResponseDetail({ id, onClose }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!id) return;
    api.get(`/admin/responses/detail/${id}`).then(r => setData(r.data));
  }, [id]);

  const downloadPDF = () => {
    const token = localStorage.getItem("se_token");
    fetch(`${API_BASE}/admin/responses/detail/${id}/pdf?lang=${lang}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `response_${data?.anonymous_code || id}.pdf`;
        a.click();
      });
  };

  return (
    <Dialog open={!!id} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="response-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center justify-between gap-3">
            <span>{lang === "id" ? "Detail Respons" : "Response Detail"}</span>
            <Button size="sm" variant="outline" onClick={downloadPDF} data-testid="response-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground">…</div> : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={lang === "id" ? "Kode" : "Code"} value={<span className="font-mono">{data.anonymous_code}</span>} />
              <Stat label={lang === "id" ? "Instrumen" : "Instrument"} value={lang === "id" ? data.instrument?.name_id : data.instrument?.name_en} />
              <Stat label={lang === "id" ? "Versi" : "Version"} value={data.instrument?.version ? `v${data.instrument.version}` : "—"} />
              <Stat label="Status" value={data.status} />
              <Stat
                label={lang === "id" ? "Bahasa pengisian" : "Response language"}
                value={
                  <span data-testid="response-detail-language" title={(data.result_languages || []).length ? `${lang === "id" ? "Hasil dilihat dalam" : "Result viewed in"}: ${data.result_languages.map((c) => responseLanguageName(c, lang)).join(", ")}` : undefined}>
                    {responseLanguageLabel(data, lang)}
                    {data.language_switched && <span className="text-xs text-muted-foreground"> ({lang === "id" ? "berganti bahasa" : "switched"})</span>}
                  </span>
                }
              />
              <Stat label={lang === "id" ? "Mulai" : "Started"} value={fmtWIB(data.started_at, lang)} />
              <Stat label={lang === "id" ? "Selesai" : "Completed"} value={fmtWIB(data.completed_at, lang)} />
              <Stat label={lang === "id" ? "Durasi" : "Duration"} value={data.started_at && data.completed_at ? fmtDuration((new Date(data.completed_at) - new Date(data.started_at)) / 1000) : "—"} />
              <Stat label={lang === "id" ? "Skor total" : "Total score"} value={data.calculated_result?.total_score ?? "—"} />
            </div>

            {data.result_view && <ResultSeen view={data.result_view} lang={lang} />}

            <div>
              <div className="font-display text-lg mb-2">{lang === "id" ? "Informasi Responden" : "Respondent Information"}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm p-4 rounded-xl bg-secondary/50 border border-border">
                {Object.entries(data.demographics || {}).length === 0 ? (
                  <div className="text-muted-foreground">{lang === "id" ? "Tidak ada data demografi." : "No demographics."}</div>
                ) : Object.entries(data.demographics).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs uppercase text-muted-foreground">{k}</div>
                    <div>{v || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            {data.consent && (
              <div>
                <div className="font-display text-lg mb-2">Consent</div>
                <div className="p-4 rounded-xl bg-secondary/50 border border-border text-sm space-y-1">
                  <div className="text-xs text-muted-foreground">Timestamp: {fmtWIB(data.consent.timestamp, lang)}</div>
                  {Object.entries(data.consent.checkboxes || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className={v ? "text-sage-500" : "text-muted-foreground"}>{v ? "✓" : "○"}</span>
                      <span>{k}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="font-display text-lg mb-2">{lang === "id" ? "Jawaban per Item" : "Item Responses"}</div>
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Section</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">{lang === "id" ? "Pertanyaan" : "Question"}</th>
                      <th className="text-left p-2">{lang === "id" ? "Jawaban" : "Answer"}</th>
                      <th className="text-right p-2">Score</th>
                      <th className="text-left p-2">Dim</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((it, i) => (
                      <tr key={i} data-testid={`item-${it.code}`}>
                        <td className="p-2 text-xs text-muted-foreground">{lang === "id" ? it.section_name_id : it.section_name_en}</td>
                        <td className="p-2 font-mono text-xs">{it.code}</td>
                        <td className="p-2 max-w-md">{lang === "id" ? it.text_id : it.text_en}</td>
                        <td className="p-2 text-xs">
                          {it.option_label_id ? (lang === "id" ? it.option_label_id : (it.option_label_en || it.option_label_id)) : String(it.raw_value ?? "—")}
                          {it.reverse_scored && <Badge variant="outline" className="ml-1 text-[9px] uppercase">Rev</Badge>}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {it.raw_score != null && it.reverse_scored ? `${it.raw_score}→${it.final_score}` : (it.final_score ?? "—")}
                        </td>
                        <td className="p-2 text-xs">{(it.dimensions || []).map(d => d.code).join(", ") || "—"}</td>
                      </tr>
                    ))}
                    {data.items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">{lang === "id" ? "Belum ada jawaban." : "No answers."}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {data.calculated_result?.dimensions?.length > 0 && (
              <div>
                <div className="font-display text-lg mb-2">{lang === "id" ? "Hasil Kalkulasi" : "Calculated Result"}</div>
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr><th className="text-left p-2">Dimension</th><th className="text-right p-2">Raw</th><th className="text-right p-2">Avg</th><th className="text-right p-2">Items</th><th className="text-left p-2">Category</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.calculated_result.dimensions.map(d => (
                        <tr key={d.dimension_id}>
                          <td className="p-2">{lang === "id" ? d.name_id : d.name_en}</td>
                          <td className="p-2 text-right font-mono">{d.raw_score}</td>
                          <td className="p-2 text-right font-mono">{d.average}</td>
                          <td className="p-2 text-right text-xs text-muted-foreground">{d.item_count}/{d.max_possible}</td>
                          <td className="p-2 text-xs">{(d.category || {})[`label_${lang}`] || (d.category || {}).label_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const pick = (source, field, lang) => {
  const other = lang === "en" ? "id" : "en";
  return source?.[`${field}_${lang}`] || source?.[`${field}_${other}`] || "";
};

/** The card, level and per-aspect levels the respondent saw on their result page. */
export function ResultSeen({ view, lang }) {
  const id = lang === "id";
  const { card, level, aspects } = view;
  const image = card ? (card[`image_${lang}_url`] || card.image_id_url || card.image_en_url) : "";
  const headline = [
    level && { label: "Level", value: <>{pick(level, "label", lang) || level.code} <span className="text-xs text-muted-foreground font-normal">· {level.total_score}{level.total_percentage != null ? ` (${level.total_percentage}%)` : ""}</span></> },
    view.type_code && { label: id ? "Tipe" : "Type", value: <span className="font-mono">{view.type_code}</span> },
    view.holland_code && { label: id ? "Kode Holland" : "Holland code", value: <span className="font-mono">{view.holland_code}</span> },
    view.dominant && { label: id ? "Profil dominan" : "Dominant profile", value: pick(view.dominant, "name", lang) },
  ].filter(Boolean);
  return (
    <div data-testid="response-result-seen">
      <div className="font-display text-lg mb-2">{id ? "Hasil yang Dilihat Responden" : "Result Shown to Respondent"}</div>
      <div className="p-4 rounded-xl bg-secondary/50 border border-border space-y-4">
        {card && (
          <div className="flex items-center gap-4" data-testid="response-result-card">
            {image && <img src={image} alt="" loading="lazy" className="w-20 h-20 object-contain rounded-lg bg-card border border-border shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{id ? "Kartu hasil" : "Result card"}</div>
              {pick(card, "badge", lang) && <div className="text-[11px] font-semibold uppercase tracking-wider mt-0.5" style={card.color ? { color: card.color } : undefined}>{pick(card, "badge", lang)}</div>}
              <div className="font-display text-xl break-words">{pick(card, "title", lang) || "—"}</div>
              {pick(card, "subtitle", lang) && <div className="text-sm text-muted-foreground break-words">{pick(card, "subtitle", lang)}</div>}
              {card.result_key && <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{card.result_key}</div>}
            </div>
          </div>
        )}
        {!card && view.card_unresolved && (
          <div className="text-sm text-muted-foreground">{id ? "Tidak ada kartu yang cocok untuk kombinasi ini." : "No card matched this combination."}</div>
        )}
        {headline.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {headline.map((h) => (
              <div key={h.label}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{h.label}</div>
                <div className="text-sm font-medium mt-0.5">{h.value}</div>
              </div>
            ))}
          </div>
        )}
        {aspects?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{id ? "Level per aspek" : "Level per aspect"}</div>
            <div className="flex flex-wrap gap-2">
              {aspects.map((a) => (
                <span key={a.dimension_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-card border border-border text-xs" data-testid={`response-aspect-${a.dimension_id}`}>
                  <span>{pick(a, "name", lang)}{a.percentage != null ? ` (${Math.round(a.percentage)}%)` : ""}</span>
                  <span className="font-semibold text-terracotta-700">{pick(a.level, "label", lang) || "—"}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-3 rounded-lg bg-card border border-border">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium mt-0.5">{value}</div>
  </div>
);
